import React, { FC, useEffect, useState } from 'react';
import { Table, Card, Spin, Empty, Tag, Button, Modal, Form, Input, Select, Space, Alert } from 'antd';
import { EditOutlined, PlusCircleOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from '../../core/types';
import { getSchoolContacts, createSchoolContact, updateSchoolContact } from '../../core/school/schoolApi';
import { loadSingleContact, resetSingleContact } from '../../core/school/schoolSlice';
import { SchoolContact } from '../../core/school/types';
import { POSITIONS } from '../../core/utils/helpers';
import './style.scss';

const SchoolContacts = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { contacts, isContactsFetching, singleContact, singleSchool } = useSelector((state: CombinedStates) => state.school);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [form] = Form.useForm();

    const schoolId = singleSchool?.id;

    useEffect(() => {
        if (schoolId) {
            dispatch(getSchoolContacts(schoolId));
        }
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (singleContact && isModalVisible) {
            form.setFieldsValue({
                ...singleContact,
            });
        }
    }, [singleContact, isModalVisible, form]);

    const getPositionLabel = (position: string) => POSITIONS[position as keyof typeof POSITIONS] || position;

    const getPositionColor = (position: string) => {
        const colorMap: { [key: string]: string } = {
            director: 'red',
            deputy_director: 'volcano',
            secretary: 'blue',
            it_specialist: 'green',
            administrator: 'purple',
            accountant: 'cyan',
            head_teacher: 'geekblue',
            counselor: 'orange',
            psychologist: 'lime',
            social_worker: 'gold',
            nurse: 'pink',
            librarian: 'magenta',
            maintenance: 'default',
            security: 'processing',
            other: 'default',
        };
        return colorMap[position] || 'default';
    };

    const handleCreate = () => {
        form
            .validateFields()
            .then(values => {
                if (singleContact?.id) {
                    dispatch(updateSchoolContact({
                        id: singleContact.id,
                        school_id: schoolId,
                        ...values,
                    }));
                } else {
                    dispatch(createSchoolContact({
                        school_id: schoolId,
                        ...values,
                    }));
                }
                setIsModalVisible(false);
                form.resetFields();
                dispatch(resetSingleContact());
            })
            .catch(info => {
                console.log('Validacijos klaida:', info);
            });
    };

    const handleEdit = (id: number) => {
        dispatch(loadSingleContact(id));
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        dispatch(resetSingleContact());
    };

    const handleAddNew = () => {
        dispatch(resetSingleContact());
        setIsModalVisible(true);
    };

    const handleReload = () => {
        if (schoolId) {
            dispatch(getSchoolContacts(schoolId));
        }
    };

    const columns = [
        {
            title: 'Vardas pavardė',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Pareigos',
            dataIndex: 'position',
            key: 'position',
            render: (position: string) => (
                <Tag color={getPositionColor(position)}>
                    {getPositionLabel(position)}
                </Tag>
            ),
        },
        {
            title: 'El. paštas',
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => (
                <a href={`mailto:${email}`}>{email}</a>
            ),
        },
        {
            title: 'Telefonas',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone: string) => (
                <a href={`tel:${phone}`}>{phone}</a>
            ),
        },
        {
            title: 'Aprašymas',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (_text: any, record: SchoolContact) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record.id)}
                        size="small">
                        Redaguoti
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Card
                title="Mokyklos kontaktai"
                extra={(
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleReload}
                            title="Atnaujinti kontaktų sąrašą">
                            Atnaujinti
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusCircleOutlined />}
                            onClick={handleAddNew}>
                            Pridėti kontaktą
                        </Button>
                    </Space>
                )}>
                <Spin spinning={isContactsFetching}>
                    <Alert
                        message="Mokyklos kontaktai"
                        description={(
                            <div>
                                <p style={{ marginBottom: '8px' }}>
                                    Čia galite saugoti ir valdyti mokyklos darbuotojų kontaktinę informaciją.
                                    <strong> Šie kontaktai nėra sistemos vartotojai</strong> - jie neturi prisijungimo prie sistemos.
                                </p>
                                <p style={{ marginBottom: 0 }}>
                                    Kontaktai naudojami tik informaciniais tikslais norint gauti informaciją, susijusią su mokykla.
                                </p>
                            </div>
                        )}
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ marginBottom: '24px' }} />
                    {!contacts || contacts.length === 0 ? (
                        <Empty
                            description="Kontaktų nerasta"
                            image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={contacts}
                            rowKey="id"
                            pagination={false}
                            size="middle"
                            scroll={{ x: true }} />
                    )}
                </Spin>
            </Card>

            <Modal
                title={singleContact?.id ? 'Redaguoti kontaktą' : 'Pridėti naują kontaktą'}
                visible={isModalVisible}
                onOk={handleCreate}
                onCancel={handleCancel}
                okText={singleContact?.id ? 'Atnaujinti' : 'Pridėti'}
                cancelText="Atšaukti"
                className="contact-modal">
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Vardas pavardė"
                        name="name"
                        rules={[
                            { required: true, message: 'Įveskite vardą ir pavardę' },
                            { max: 191, message: 'Vardas ir pavardė negali viršyti 191 simbolių' },
                        ]}>
                        <Input maxLength={191} />
                    </Form.Item>

                    <Form.Item
                        label="Pareigos"
                        name="position"
                        rules={[{ required: true, message: 'Pasirinkite pareigas' }]}>
                        <Select placeholder="Pasirinkite pareigas">
                            {Object.entries(POSITIONS).map(([key, label]) => (
                                <Select.Option key={key} value={key}>
                                    {label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="El. paštas"
                        name="email"
                        rules={[
                            { required: true, message: 'Įveskite el. paštą' },
                            { type: 'email', message: 'Įveskite teisingą el. pašto adresą' },
                            { max: 191, message: 'El. paštas negali viršyti 191 simbolių' },
                        ]}>
                        <Input maxLength={191} />
                    </Form.Item>

                    <Form.Item
                        label="Telefonas"
                        name="phone"
                        rules={[
                            { required: true, message: 'Įveskite telefono numerį' },
                            { max: 191, message: 'Telefono numeris negali viršyti 191 simbolių' },
                        ]}>
                        <Input maxLength={191} />
                    </Form.Item>

                    <Form.Item
                        label="Aprašymas"
                        name="description"
                        rules={[
                            { max: 500, message: 'Aprašymas negali viršyti 500 simbolių' },
                        ]}>
                        <Input.TextArea
                            rows={3}
                            maxLength={1000}
                            showCount
                            placeholder="Papildomas aprašymas (neprivaloma)" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default SchoolContacts;
